import { useCallback } from 'react';
import { atom, useRecoilValue, useSetRecoilState } from 'recoil';
import { getPersistedValue, setPersistedValue } from './db/config';
import { Koordinaten } from './types';

export type Einheit = {
  id: string;
  name: string;
};

export type Untereinheit = {
  id: string;
  name: string;
};

export type Geraet = {
  id: string;
  name: string;
  positionName: string;
  untereinheit?: Untereinheit;
  einheit: Einheit;
  homebase?: Koordinaten;
};

export const moduleOptions = [
  'dashboard',
  'checklisten',
  'gefahrgut',
  'gefahrgutlight',
  'rettungskarten',
  'kartenmaterial',
  'navigation',
  'kontakte',
  'dokumente',
  'warnkarte',
  'extnavigation',
  'verzeichnis',
  'formulare',
  'patienten',
  'atemschutz',
  'info',
  'meldungen',
  'meldungenadmin',
  'statistics',
  'whiteboard',
] as const;

export type Modul = (typeof moduleOptions)[number];

export type MapLayerProviderData = {
  id: string;
  label: string;
};

export type HTTPAuth = { type: 'basic'; username: string; password: string } | { type: 'bearerToken'; token: string };

export type WMSMapLayerProvider = MapLayerProviderData & {
  type: 'wms';
  url: string;
  auth?: HTTPAuth;
  crs?: string;
  layers: WMSMapLayer[];
};

export type WMSMapLayer = {
  id: string;
  label: string;
  layer: string;
  opacity?: number;
  attribution?: string;
};

export type ShapeMapLayerProvider = MapLayerProviderData & {
  type: 'shape';
  layers: ShapeMapLayer[];
};

export type ShapeMapLayer = {
  id: string;
  label: string;
  url: string;
  color?: string;
  opacity?: number;
  attribution?: string;
};

export type MapLayerProvider = WMSMapLayerProvider | ShapeMapLayerProvider;

export type ExternalPage = {
  label: string;
  url: string;
  target?: 'app' | 'external';
  showInMenu?: boolean;
};

export type LicenseData = Geraet & {
  portalKey: string;
  alarmKey: string;
  missionApiKey?: string;
  fireboardKey?: string;
  module: Modul[];
  warnkarte: string[];
  gueltigkeit?: number;
  positionSenden: boolean;
  positionSehen: boolean;
  freeversion: boolean;
  demo: boolean;
  offlineArea?: number[][];
  mapLayers?: MapLayerProvider[];
  features?: string[];
  externalPages?: ExternalPage[];
};

export const licenseAtom = atom<LicenseData | undefined>({
  key: 'license',
  default: getPersistedValue('license', undefined),
});

export function useLicense(): LicenseData | undefined {
  return useRecoilValue(licenseAtom);
}

export function useSetLicense(): (license?: LicenseData) => Promise<void> {
  const setLicense = useSetRecoilState(licenseAtom);
  return useCallback(
    async (license) => {
      await setPersistedValue('license', license);
      setLicense(license);
    },
    [setLicense]
  );
}
